import { useState } from 'react';
import { Layers as LayersIcon, Globe, Box, AlertTriangle, ShieldAlert } from 'lucide-react';

/**
 * MapLayerToggle — Sprint Layers (Feb 2026).
 *
 * Floating HUD control to switch the Hub3DMap basemap between two
 * GPU-cost-tiers :
 *  - vector       → google.maps.Map (vector tiles + 3D buildings, ~10x lighter)
 *  - photoreal    → @vis.gl/react-google-maps Map3D (Photorealistic 3D Tiles)
 *
 * Props
 *  - value : 'vector' | 'photoreal' (controlled)
 *  - onChange : (next) => void
 *  - locked : boolean  — true when MapVRAMGuard forced a downgrade
 *  - lockedReason : optional string used in the tooltip
 *  - missingMapId : boolean — true if user picked vector but no mapId is set
 */
const LAYERS = [
  {
    id: 'vector',
    label: 'Vector 3D',
    sub: 'Léger · Recommandé',
    icon: Box,
    tone: 'gold',
  },
  {
    id: 'photoreal',
    label: 'Photoréaliste',
    sub: 'Immersif · WebGL+',
    icon: Globe,
    tone: 'info',
  },
];

const MapLayerToggle = ({
  value = 'vector',
  onChange,
  locked = false,
  lockedReason = null,
  missingMapId = false,
  photorealFailed = false,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="absolute bottom-4 left-4 z-[470] flex flex-col items-start gap-2 pointer-events-auto"
      data-testid="map-layer-toggle"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors"
        style={{
          background: 'rgba(10,10,13,0.85)',
          backdropFilter: 'blur(10px)',
          color: 'var(--c-gold, #D9A05B)',
          border: '1px solid var(--c-border-accent, rgba(217,160,91,0.40))',
          boxShadow: '0 4px 14px -2px rgba(0,0,0,0.5)',
        }}
        data-testid="map-layer-toggle-btn"
        aria-expanded={open}
        title={locked ? `Carte verrouillée en Vector 3D — ${lockedReason || 'autre module WebGL actif'}` : 'Choisir le fond de carte'}
      >
        <LayersIcon className="w-3.5 h-3.5" />
        Layers
        {locked && (
          <ShieldAlert className="w-3 h-3" style={{ color: '#FCA5A5' }} />
        )}
      </button>

      {open && (
        <div
          className="rounded-xl p-2 min-w-[240px]"
          style={{
            background: 'rgba(10,10,13,0.94)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(217,160,91,0.30)',
            boxShadow: '0 12px 32px -8px rgba(0,0,0,0.7)',
          }}
          data-testid="map-layer-menu"
        >
          {LAYERS.map((l) => {
            const Icon = l.icon;
            const isActive = value === l.id;
            const isDisabled = locked && l.id === 'photoreal';
            const isMissing = l.id === 'vector' && missingMapId;
            const accent = l.tone === 'gold' ? 'var(--c-gold)' : '#93C5FD';
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => {
                  if (isDisabled) return;
                  onChange?.(l.id);
                  setOpen(false);
                }}
                disabled={isDisabled}
                data-testid={`map-layer-option-${l.id}`}
                className="w-full flex items-center gap-3 px-2.5 py-2 rounded-md transition-colors disabled:opacity-40"
                style={{
                  background: isActive ? 'rgba(217,160,91,0.14)' : 'transparent',
                  border: `1px solid ${isActive ? 'var(--c-border-accent)' : 'transparent'}`,
                }}
                onMouseEnter={(e) => { if (!isActive && !isDisabled) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon className="w-4 h-4 shrink-0" style={{ color: isActive ? accent : 'var(--c-text-muted, rgba(255,255,255,0.5))' }} />
                <span className="flex-1 text-left min-w-0">
                  <span
                    className="block text-[12px] font-semibold"
                    style={{ color: isActive ? 'var(--c-text)' : 'var(--c-text, rgba(255,255,255,0.9))' }}
                  >
                    {l.label}
                    {isActive && (
                      <span
                        className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full align-middle"
                        style={{ background: accent }}
                      />
                    )}
                  </span>
                  <span className="block text-[10.5px] mt-0.5" style={{ color: 'var(--c-text-faint, rgba(255,255,255,0.45))' }}>
                    {l.sub}
                  </span>
                </span>
                {isMissing && (
                  <span title="Map ID vectoriel non configuré dans .env">
                    <AlertTriangle className="w-3 h-3" style={{ color: '#FDE68A' }} />
                  </span>
                )}
              </button>
            );
          })}

          {locked && (
            <div
              className="mt-2 px-2.5 py-2 rounded-md text-[10.5px] leading-relaxed flex items-start gap-2"
              style={{
                background: 'rgba(239,68,68,0.10)',
                border: '1px solid rgba(239,68,68,0.30)',
                color: '#FCA5A5',
              }}
              data-testid="map-layer-locked-banner"
            >
              <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                <strong>Mode dégradé.</strong> Un autre module WebGL est actif
                ({lockedReason || 'éditeur 3D, annotation…'}) — la carte est
                forcée en Vector 3D pour libérer la VRAM.
              </span>
            </div>
          )}

          {missingMapId && !locked && (
            <div
              className="mt-2 px-2.5 py-2 rounded-md text-[10.5px] leading-relaxed flex items-start gap-2"
              style={{
                background: 'rgba(217,160,91,0.10)',
                border: '1px solid var(--c-border-accent)',
                color: '#FDE68A',
              }}
              data-testid="map-layer-missing-mapid"
            >
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                <strong>Map ID vectoriel manquant.</strong> Ajoutez{' '}
                <code className="px-1 rounded" style={{ background: 'rgba(0,0,0,0.4)' }}>
                  REACT_APP_GOOGLE_VECTOR_MAP_ID
                </code>{' '}
                dans <code>frontend/.env</code> pour activer les bâtiments 3D vectoriels.
                En attendant, fallback sur tuiles Leaflet.
              </span>
            </div>
          )}

          {photorealFailed && (
            <div
              className="mt-2 px-2.5 py-2 rounded-md text-[10.5px] leading-relaxed flex items-start gap-2"
              style={{
                background: 'rgba(239,68,68,0.10)',
                border: '1px solid rgba(239,68,68,0.30)',
                color: '#FCA5A5',
              }}
              data-testid="map-layer-photoreal-failed"
            >
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                <strong>Tuiles 3D photoréalistes indisponibles.</strong> Activez{' '}
                <a
                  href="https://console.cloud.google.com/apis/library/tile.googleapis.com"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                  style={{ color: 'var(--c-gold)' }}
                >
                  l'API "Map Tiles"
                </a>{' '}
                dans GCP, puis rechargez. Repli automatique sur Vector 3D.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapLayerToggle;
